import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { KeyRound, Copy, Check, Plus, Loader2, Trash2, Power } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const authHeader = () => {
  const t = localStorage.getItem('savenes_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

export default function AccessCodesPage() {
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [copiedCode, setCopiedCode] = useState('');
  const [form, setForm] = useState({ label: '', max_uses: '', expires_in_days: '30' });

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/access-codes`, { headers: authHeader() });
      setCodes(r.data.codes || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not load codes');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    setBusy(true);
    try {
      const payload = { label: form.label || null };
      if (form.max_uses) payload.max_uses = parseInt(form.max_uses, 10);
      if (form.expires_in_days) payload.expires_in_days = parseInt(form.expires_in_days, 10);
      await axios.post(`${API_URL}/api/access-codes`, payload, { headers: authHeader() });
      toast.success('Access code created');
      setCreateOpen(false);
      setForm({ label: '', max_uses: '', expires_in_days: '30' });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not create code');
    } finally { setBusy(false); }
  };

  const toggleActive = async (code) => {
    try {
      await axios.patch(`${API_URL}/api/access-codes/${code.id}?active=${!code.active}`, {}, { headers: authHeader() });
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Could not update'); }
  };

  const remove = async (code) => {
    if (!window.confirm(`Delete code ${code.code}? Existing pending requests will not be approvable until you re-issue.`)) return;
    try {
      await axios.delete(`${API_URL}/api/access-codes/${code.id}`, { headers: authHeader() });
      toast.success('Code deleted');
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Could not delete'); }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(code);
    setTimeout(() => setCopiedCode(''), 1500);
  };

  return (
    <div className="p-6 space-y-6" data-testid="access-codes-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#0A2342]">Access Codes</h1>
          <p className="text-sm text-[#64748B] mt-1">Generate codes that let new employees request to join your organization.</p>
        </div>
        <Button onClick={() => setCreateOpen(true)} className="bg-[#0E7490] hover:bg-[#0E7490]/90 text-white" data-testid="new-code-btn">
          <Plus className="w-4 h-4 mr-2" /> New code
        </Button>
      </div>

      {loading && <div className="text-center py-12 text-[#64748B]"><Loader2 className="w-6 h-6 animate-spin inline mr-2" /> Loading…</div>}

      {!loading && codes.length === 0 && (
        <div className="text-center py-16 bg-white rounded-xl border border-[#E2E8F0]">
          <KeyRound className="w-10 h-10 text-[#94A3B8] mx-auto mb-3" />
          <p className="text-[#64748B]">No access codes yet.</p>
          <p className="text-xs text-[#94A3B8] mt-1">Create one to let employees request to join your organization.</p>
        </div>
      )}

      <div className="grid gap-3">
        {codes.map(c => {
          const expired = c.expires_at && new Date(c.expires_at) <= new Date();
          const maxedOut = c.max_uses && c.used_count >= c.max_uses;
          const usable = c.active && !expired && !maxedOut;
          return (
            <Card key={c.id} className="p-4 border-[#E2E8F0] flex items-center gap-4" data-testid={`code-row-${c.id}`}>
              <div className="w-10 h-10 rounded-lg bg-[#0E7490]/10 flex items-center justify-center shrink-0">
                <KeyRound className="w-5 h-5 text-[#0E7490]" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <code className="font-mono font-semibold text-[#0A2342] text-base">{c.code}</code>
                  <Button size="sm" variant="ghost" onClick={() => copyCode(c.code)} data-testid={`copy-${c.id}`}>
                    {copiedCode === c.code ? <Check className="w-4 h-4 text-[#10B981]" /> : <Copy className="w-4 h-4" />}
                  </Button>
                  {!usable && <Badge variant="outline" className="text-xs bg-rose-50 text-rose-700 border-rose-200">
                    {!c.active ? 'Disabled' : expired ? 'Expired' : 'Maxed out'}
                  </Badge>}
                  {usable && <Badge className="text-xs bg-emerald-100 text-emerald-700 border-emerald-200">Active</Badge>}
                </div>
                <p className="text-xs text-[#64748B] mt-1">
                  {c.label && <span>{c.label} · </span>}
                  Used {c.used_count}{c.max_uses ? ` / ${c.max_uses}` : ''}
                  {c.expires_at && <span> · Expires {new Date(c.expires_at).toLocaleDateString()}</span>}
                </p>
              </div>
              <div className="flex items-center gap-1">
                <Button size="sm" variant="ghost" onClick={() => toggleActive(c)} title={c.active ? 'Disable' : 'Enable'} data-testid={`toggle-${c.id}`}>
                  <Power className={`w-4 h-4 ${c.active ? 'text-[#10B981]' : 'text-[#94A3B8]'}`} />
                </Button>
                <Button size="sm" variant="ghost" onClick={() => remove(c)} title="Delete" data-testid={`delete-${c.id}`}>
                  <Trash2 className="w-4 h-4 text-rose-500" />
                </Button>
              </div>
            </Card>
          );
        })}
      </div>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent data-testid="new-code-dialog">
          <DialogHeader>
            <DialogTitle>Create access code</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div>
              <Label htmlFor="cl">Label (optional, shown to you only)</Label>
              <Input id="cl" placeholder="e.g. Q1 2026 nurses" value={form.label} onChange={e => setForm(f => ({ ...f, label: e.target.value }))} data-testid="code-label" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="mu">Max uses (optional)</Label>
                <Input id="mu" type="number" min="1" placeholder="Unlimited" value={form.max_uses} onChange={e => setForm(f => ({ ...f, max_uses: e.target.value }))} data-testid="code-max-uses" />
              </div>
              <div>
                <Label htmlFor="ex">Expires in (days)</Label>
                <Input id="ex" type="number" min="1" max="365" value={form.expires_in_days} onChange={e => setForm(f => ({ ...f, expires_in_days: e.target.value }))} data-testid="code-expires" />
              </div>
            </div>
            <p className="text-xs text-[#64748B]">
              Share the generated code (e.g. <code className="bg-[#F8FAFC] px-1 rounded">SVN-A4F2-9X7Q</code>) with employees. They'll submit a join request that you can approve from <strong>Access Requests</strong>.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)} disabled={busy}>Cancel</Button>
            <Button onClick={create} disabled={busy} className="bg-[#0E7490] hover:bg-[#0E7490]/90 text-white" data-testid="create-code-submit">
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null} Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
